import type { RosterAgent, SidebarSettings } from "@ark-bot/protocol";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { digitShortcut, matchesSwitcher, shortcutLabel, switcherOrder } from "../navigation.js";
import { previewOf } from "../roster.js";
import { SWITCHER } from "../text/switcher.js";

import { ActiveGlide } from "./ActiveGlide.js";
import { Avatar } from "./Avatar.js";

interface Props {
  readonly agents: readonly RosterAgent[];
  readonly sidebar: SidebarSettings;
  readonly selectedId: string | null;
  readonly closing: boolean;
  readonly onOpen: (id: string) => void;
  readonly onClose: () => void;
}

/**
 * The bot switcher: ⌘K or Ctrl+K from anywhere, type a few letters of a
 * name, a role or what a bot last said, and Enter opens it. The first nine
 * wear ⌘1 to ⌘9, in the order the sidebar shows them, so a bot's number is
 * where it sits.
 */
export function Switcher({ agents, sidebar, selectedId, closing, onOpen, onClose }: Props): React.JSX.Element {
  const t = useText(SWITCHER);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const hidden = new Set(sidebar.hidden);
  const matches = switcherOrder(agents, sidebar).filter((agent) => matchesSwitcher(agent, query));
  useBackClose(true, onClose);

  useEffect(() => input.current?.focus(), []);
  // A new query starts at the top of its list.
  useEffect(() => setIndex(0), [query]);
  // The row the keys are on stays in view.
  useLayoutEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  const open = (agent: RosterAgent | undefined): void => {
    if (agent == null) return;
    onOpen(agent.id);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    const digit = digitShortcut(event);
    if (digit != null) {
      event.preventDefault();
      open(matches[digit]);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (matches.length === 0) return;
      setIndex((current) => (current + (event.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      open(matches[index]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className={`switcher-backdrop${closing ? " closing" : ""}`} onClick={onClose}>
      <div className="switcher" role="dialog" aria-label={t.title} onClick={(event) => event.stopPropagation()} onKeyDown={onKeyDown}>
        <label className="switcher-search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5 14 14" />
          </svg>
          <input
            ref={input}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.search}
            aria-label={t.search}
            role="combobox"
            aria-expanded="true"
            aria-controls="switcher-list"
            aria-activedescendant={matches[index] == null ? undefined : `switcher-${matches[index].id}`}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="switcher-kbd">Esc</kbd>
        </label>
        <ul className="switcher-list" id="switcher-list" role="listbox" ref={list}>
          <ActiveGlide item selector=".switcher-row.active" className="glide-hover" />
          {matches.map((agent, at) => {
            const preview = previewOf(agent);
            const attention = preview?.attention === true;
            const role = agent.config.title?.trim() ?? "";
            return (
              <li
                key={agent.id}
                id={`switcher-${agent.id}`}
                role="option"
                aria-selected={at === index}
                data-index={at}
                className={`switcher-row${at === index ? " active" : ""}${agent.id === selectedId ? " current" : ""}`}
                onMouseMove={() => setIndex(at)}
                onClick={() => open(agent)}
              >
                <span className={`agent-face-wrap${agent.busy ? " working" : ""}`}>
                  <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={32} />
                  {agent.busy ? <span className="work-ring" aria-hidden="true" /> : null}
                </span>
                <span className="switcher-body">
                  <span className="switcher-line">
                    <strong className="switcher-name">
                      {agent.kind === "group" ? <span className="agent-hash">#</span> : null}
                      {agent.name}
                    </strong>
                    {role.length > 0 ? <span className="agent-role tag">{role}</span> : null}
                    {attention ? <span className="switcher-state attention">{t.needsYou}</span> : agent.busy ? <span className="switcher-state working shimmer">{t.working}</span> : hidden.has(agent.id) ? <span className="switcher-state">{t.hidden}</span> : null}
                  </span>
                  {preview != null && preview.text.length > 0 ? <span className="switcher-preview">{preview.text}</span> : null}
                </span>
                {at < 9 ? <kbd className="switcher-kbd">{shortcutLabel(String(at + 1))}</kbd> : null}
              </li>
            );
          })}
          {matches.length === 0 ? <li className="switcher-empty">{agents.length === 0 ? t.noBots : t.nothing(query)}</li> : null}
        </ul>
        <div className="switcher-hint">{t.hint}</div>
      </div>
    </div>
  );
}
